import { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    getDepartmentVariableTemplate,
    getDepartmentVariables,
    Variable,
} from '../../services/api/departmentVariablesService';
import { getUsersByDepartment } from '../../services/apiService';
import { DepartmentUser } from './setup-wizard/WizardVariableForm';
import SetupWizard from './setup-wizard/SetupWizard';
import SettingsOverview from './SettingsOverview';
import LoadingRing from '../ui-components/LoadingRing';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { VariableSection } from './utils/variableTypes';
import { hydrateTemplate } from './utils/hydrateTemplate';
import { EVENT_KEYS, trackLoad } from '../../services/api/trackingService';

type Mode = 'loading' | 'wizard' | 'overview';

export default function DepartmentSettingsPage() {
    const [savedVariables, setSavedVariables] = useState<Variable[]>([]);
    const [users, setUsers] = useState<DepartmentUser[]>([]);
    const [sections, setSections] = useState<VariableSection[]>([]);
    const [mode, setMode] = useState<Mode>('loading');
    const { toast } = useToast();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const refetchVariables = async () => {
        const data = await getDepartmentVariables();
        setSavedVariables(data);
    };

    useEffect(() => {
        trackLoad(EVENT_KEYS.viewDepartmentVariables, {});
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const [varsResult, usersResult] = await Promise.allSettled([
                getDepartmentVariables(),
                getUsersByDepartment(),
            ]);
            const vars =
                varsResult.status === 'fulfilled' ? varsResult.value : [];
            setSavedVariables(vars);
            setUsers(
                usersResult.status === 'fulfilled' &&
                    Array.isArray(usersResult.value)
                    ? usersResult.value
                    : []
            );
            const templateResult = await getDepartmentVariableTemplate().catch(
                (): null => null
            );
            const templateSections =
                templateResult != null
                    ? hydrateTemplate(templateResult, t)
                    : [];
            setSections(templateSections);

            if (
                varsResult.status === 'rejected' ||
                usersResult.status === 'rejected' ||
                templateSections.length === 0
            ) {
                toast({
                    title: t('departmentVariables.errors.failedToLoad'),
                    variant: 'destructive',
                });
            }

            const hasVariables = vars.length > 0;
            const canRenderWizard = templateSections.length > 0;
            setMode(!hasVariables && canRenderWizard ? 'wizard' : 'overview');
        };
        fetchData();
    }, [toast]);

    const onSaved = async () => {
        try {
            await refetchVariables();
        } catch {
            toast({
                title: t('departmentVariables.errors.failedToSave'),
                variant: 'destructive',
            });
        }
    };

    const onWizardComplete = async () => {
        try {
            await refetchVariables();
            setMode('overview');
        } catch {
            toast({
                title: t('departmentVariables.errors.failedToFinishSetup'),
                variant: 'destructive',
            });
        }
    };

    if (mode === 'loading') {
        return (
            <div className="flex justify-center items-center min-h-[60vh]">
                <LoadingRing size="large" />
            </div>
        );
    }

    return (
        <div className="container mx-auto max-w-3xl px-4 py-8">
            {/* Page Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold">{t('departmentVariables.title')}</h1>
                    {/* Back link */}
                    <Button
                        variant="outline"
                        onClick={() => navigate('/department-policies')}
                        size="sm"
                        className="gap-1.5"
                    >
                        <ChevronLeft className="h-4 w-4" />
                        {t('departmentVariables.backToPolicies')}
                    </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                    {t('departmentVariables.subtitle')}
                </p>
            </div>

            {mode === 'wizard' ? (
                <SetupWizard
                    savedVariables={savedVariables}
                    users={users}
                    sections={sections}
                    initialStep={0}
                    onStepSaved={refetchVariables}
                    onComplete={onWizardComplete}
                />
            ) : (
                <SettingsOverview
                    savedVariables={savedVariables}
                    users={users}
                    sections={sections}
                    onSaved={onSaved}
                />
            )}
        </div>
    );
}
